import type { AdminUserPlan } from '@steado/admin-contract/user-detail';

import { FieldRow, FieldRows, Panel } from '@/components/panel/panels';
import { BadgeCell } from '@/components/table/cells';

import { MEAL_RENEWAL_LABELS, WORKOUT_RENEWAL_LABELS } from './plan-renewal-labels';

interface PlanRenewalPanelProps {
  readonly renewal: AdminUserPlan['renewal'];
  readonly entitlement: AdminUserPlan['entitlement'];
  readonly mealPlanMode: AdminUserPlan['mealPlanMode'];
}

/**
 * What the weekly sweep will do with this user, and the two facts that decide
 * it.
 *
 * `entitlement` and `mealPlanMode` sit above the tracks rather than on the
 * profile tab, because neither answers the question alone: access decides
 * whether anything is generated, the mode decides whether the generated week
 * carries meals, and reading one without the other is how an operator
 * concludes "Premium, so they should have meals".
 *
 * The two tracks are separate rows because they are two plans on two cycles —
 * meals can start on Monday and workouts on Wednesday, each falling due on its
 * own date.
 */
export function PlanRenewalPanel({ renewal, entitlement, mealPlanMode }: PlanRenewalPanelProps) {
  return (
    <Panel title="Plan renewal" bleed>
      <FieldRows>
        <FieldRow
          label="Access"
          value={<BadgeCell label={entitlement} tone={entitlement === 'premium' ? 'purple' : 'mute'} />}
        />
        <FieldRow label="Meal plan mode" value={mealPlanMode} mono />
        <TrackRow
          label="Workouts"
          renews={renewal.workouts.renews}
          reason={renewal.workouts.reason === null ? null : WORKOUT_RENEWAL_LABELS[renewal.workouts.reason]}
        />
        <TrackRow
          label="Meals"
          renews={renewal.meals.renews}
          reason={renewal.meals.reason === null ? null : MEAL_RENEWAL_LABELS[renewal.meals.reason]}
        />
      </FieldRows>
    </Panel>
  );
}

interface TrackRowProps {
  readonly label: string;
  readonly renews: boolean;
  readonly reason: string | null;
}

/**
 * A track reads as the badge or as its reason, never both: `reason` is null
 * exactly when the track renews, so one of the two is always the whole answer.
 */
function TrackRow({ label, renews, reason }: TrackRowProps) {
  if (reason !== null) return <FieldRow label={label} value={reason} tone="muted" />;

  return <FieldRow label={label} value={renews ? <BadgeCell label="renews" tone="ok" dot /> : null} />;
}
